import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Video, Laptop, Users, CheckCircle, ArrowRight, Star, Award, Clock, Sparkles, BookOpen, PlayCircle, Guitar, Quote, ChevronDown, ChevronUp, BadgeEuro, GraduationCap } from "lucide-react";
import { InteractivePathSection } from "@/components/InteractivePathSection";
import { TermineSection } from "@/components/TermineSection";
import { useState } from "react";
import Autoplay from "embla-carousel-autoplay";
import heroImage from "@/assets/fachkraft-musik-hero.jpg.asset.json";
import teamImage from "@/assets/fachkraft-musik-team.jpg";
import zertifikatImage from "@/assets/fachkraft-musik-zertifikat.jpg.asset.json";
import signaturesImage from "@/assets/team-signatures.jpg";
import notesImg from "@/assets/notes-watercolor.png";
import drumImg from "@/assets/drum-watercolor.png";
import awardLogo from "@/assets/award-studienpreis-2025.png";
import zfuLogo from "@/assets/zfu-logo.jpg";
import marionFriedsam from "@/assets/testimonials/marion-friedsam.jpg";
import eviWagner from "@/assets/testimonials/evi-wagner.jpg";
import annaWolter from "@/assets/testimonials/anna-wolter.jpg";
import sandraLampe from "@/assets/testimonials/sandra-lampe.jpg";

// Module Images
import allesIstEinInstrument from "@/assets/modules/alles-ist-ein-instrument.jpg";
import triTraTrommel from "@/assets/modules/tri-tra-trommel.jpg";
import kommtUndLasst from "@/assets/modules/kommt-und-lasst-uns-singen.jpg";
import handtrommelRassel from "@/assets/modules/handtrommel-rassel.jpg";
import kleinAberOho from "@/assets/modules/klein-aber-oho.jpg";
import praktischeMusiktheorie from "@/assets/modules/praktische-musiktheorie.jpg";
import esWirdBunt from "@/assets/modules/es-wird-bunt.jpg";
import wirKommenInBewegung from "@/assets/modules/wir-kommen-in-bewegung.jpg";
import hoertDenSchoenen from "@/assets/modules/hoert-den-schoenen-abschiedston.jpg";
import xylophonGlockenspiel from "@/assets/modules/xylophon-glockenspielton.jpg";
import planungOrganisation from "@/assets/modules/planung-organisation.jpg";
import orffInstrumente from "@/assets/modules/orff-instrumente-leicht-lernen.jpg";
import boomwhackers from "@/assets/modules/boomwhackers-leicht-lernen.jpg";
import klapperDieCup from "@/assets/modules/klapper-die-cup.jpg";
import singendeWaldzwerge from "@/assets/modules/singende-waldzwerge.jpg";
import musiktheorieRhythmus from "@/assets/modules/musiktheorie-rhythmus.jpg";
import musiktheorieNoten from "@/assets/modules/musiktheorie-noten.jpg";
import abcDesSingens from "@/assets/modules/abc-des-singens.jpg";
import kreativerKindertanz from "@/assets/modules/kreativer-kindertanz.png";
import trommelissimo from "@/assets/modules/trommelissimo.jpg";
import bodypercussion from "@/assets/modules/bodypercussion-leicht-lernen.jpg";
import cajonBeats from "@/assets/modules/cajon-coole-beats.jpg";
import SEO from "@/components/SEO";
import { formatEuro, getBundlePricingBySlug } from "@/config/trainingBundlePricing";

/** Preise aus der Anzeige-Config (dieselben Beträge wie die Kasse, Fragen 351 und 354). */
const FFM_PREISE = getBundlePricingBySlug("fachkraft-musik")?.tarife;
const FFM_PREIS = FFM_PREISE?.standard ? formatEuro(FFM_PREISE.standard.rechnung.amountCents) : "";
const FFM_MITGLIED = FFM_PREISE?.mitglied ? formatEuro(FFM_PREISE.mitglied.rechnung.amountCents) : "";

// Module Data
const modules = [
  {
    id: 1,
    title: "Alles ist ein Instrument",
    subtitle: "Musizieren mit Alltagsgegenständen",
    image: allesIstEinInstrument,
    category: "Live-Webinare",
    description: "Lerne, wie du auch ohne \"richtige\" Musikinstrumente Lieder begleiten und Rhythmen spielen kannst. Mit vielen Spielideen, Sprechversen und neuen Kinderliedern, für die du nur genau das Material brauchst, das du gerade zur Hand hast oder es ganz leicht und kostengünstig beschaffen kannst.",
    instructor: "Kati Breuer",
    duration: "120 min"
  },
  {
    id: 2,
    title: "Tri-Tra-Trommel",
    subtitle: "Rhythmus und Trommeln",
    image: triTraTrommel,
    category: "Live-Webinare",
    description: "Lerne, wie du mit Spaß und Freude Rhythmus- und Taktgefühl, sowie einfache Spieltechniken auf den verschiedensten Trommeln kindgerecht vermitteln kannst. Mit vielen Spielideen, Liedern aus aller Welt und praktischen Tipps.",
    instructor: "Nicole Knorr",
    duration: "120 min"
  },
  {
    id: 3,
    title: "Kommt und lasst uns singen",
    subtitle: "Singen mit Kindern",
    image: kommtUndLasst,
    category: "Live-Webinare",
    description: "Lerne Wissenswertes über das Singen mit Kindern, von A wie Atem bis Z wie Zusammenklang. Mit spielerischen Übungen zur Stimmbildung, praktischen Tipps für die Liedeinführung und neuen Liedern für deine Kindergruppe.",
    instructor: "Kati Breuer",
    duration: "120 min"
  },
  {
    id: 4,
    title: "Handtrommel, Rassel & Co!",
    subtitle: "Einsatz und Umgang mit Orff-Instrumenten",
    image: handtrommelRassel,
    category: "Live-Webinare",
    description: "Lerne, wie du Orff-Instrumente sinnvoll und praxisnah einsetzen kannst. Außerdem widmen wir uns u. a. Fragen, wie: Welche Orff-Instrumente sind für welches Alter sinnvoll? Wie viele Instrumente benötige ich zum Musizieren mit Kindern?",
    instructor: "Nicole Knorr",
    duration: "120 min"
  },
  {
    id: 5,
    title: "Klein, aber oho!",
    subtitle: "Musikalische Ideen für die Jüngsten (U3)",
    image: kleinAberOho,
    category: "Live-Webinare",
    description: "Lerne Sprechverse, Lieder und Spielideen für Kinder unter 3 Jahren und erhalte Tipps zur Instrumentenauswahl für diese Altersstufe. Außerdem bekommst du Ideen an die Hand, wie du die Inhalte für ältere Kinder erweitern kannst.",
    instructor: "Kati Breuer",
    duration: "120 min"
  },
  {
    id: 6,
    title: "Praktische Musiktheorie",
    subtitle: "Grundlagen der Musiktheorie",
    image: praktischeMusiktheorie,
    category: "Live-Webinare",
    description: "Lerne, wie du mit dem nötigen musiktheoretischen Hintergrundwissen deine Praxis bereichern kannst. Wir sprechen über Notenwerte und wie sie deine rhythmische Arbeit verbessern können, ebenso wie über einzelne Töne und Akkorde und in welchem Zusammenhang sie zueinander stehen und wie du ganz einfach transponieren kannst.",
    instructor: "Daniel Sommer",
    duration: "120 min"
  },
  {
    id: 7,
    title: "Es wird bunt",
    subtitle: "Musizieren mit farbcodierten Eininstrumenten",
    image: esWirdBunt,
    category: "Live-Webinare",
    description: "Lerne, wie man spielerisch und kreativ mit Boomwhackers, Soundbellows, bunten Klangbausteinen oder Handglocken musizieren kann. Lerne geeignetes Material und passende Lieder dazu kennen.",
    instructor: "Nicole Knorr",
    duration: "120 min"
  },
  {
    id: 8,
    title: "Wir kommen in Bewegung",
    subtitle: "Bewegungslieder und Tänze",
    image: wirKommenInBewegung,
    category: "Live-Webinare",
    description: "Musik und Bewegung gehören zusammen! Lerne, wie du mit Bewegungsliedern und einfachen Tänzen die natürliche Freude von Kindern an Musik und Bewegung kombinierst. Mit vielen praktischen Ideen für Kreis- und Singspiele, Tänze und bewegte Lieder, die Kinder zum Mitmachen animieren.",
    instructor: "Kati Breuer",
    duration: "120 min"
  },
  {
    id: 9,
    title: "Hört den schönen Abschiedston",
    subtitle: "Musikalische Rituale",
    image: hoertDenSchoenen,
    category: "Live-Webinare",
    description: "Lerne, wie du mit den immer gleichen Reimen, Liedern oder Spielideen dein Musikangebot strukturieren und den Kindern dadurch Sicherheit vermitteln kannst. Mit vielen praktischen Ideen für Anfang und Ende der Musikeinheit sowie die Gestaltung von Übergängen.",
    instructor: "Kati Breuer",
    duration: "120 min"
  },
  {
    id: 10,
    title: "Xylophon- & Glockenspielton",
    subtitle: "Elementare Liedbegleitung",
    image: xylophonGlockenspiel,
    category: "Live-Webinare",
    description: "Lerne den richtigen Umgang und Einsatz von Stabspielen. Wie kannst du auf diesen Instrumenten ein Lied begleiten? Wie können Kinder einfache Lieder selbst begleiten? Passende Spiele und Ideen u. a. zur Schlägelhaltung uvm.",
    instructor: "Nicole Knorr",
    duration: "120 min"
  },
  {
    id: 11,
    title: "Planung & Organisation",
    subtitle: "Strukturierung deiner musikalischen Arbeit",
    image: planungOrganisation,
    category: "Live-Webinare",
    description: "Das \"Drum-herum\" ist vor allem bei der selbstständigen Durchführung von Musikangeboten nicht zu unterschätzen. Lohnt es sich, selbstständig Kurse anzubieten? Was muss ich vertraglich alles beachten? Brauche ich eine eigene Homepage? Diese und weitere Fragen rund um das Thema beantworten wir in diesem Webinar.",
    instructor: "Daniel Sommer",
    duration: "120 min"
  },
  {
    id: 12,
    title: "Orff-Instrumente leicht lernen",
    subtitle: "Orff-Instrumente in Kita & Krippe",
    image: orffInstrumente,
    category: "Selbstlernmodule",
    description: "In diesem Kurs lernst du, wie man Orff-Instrumente im Kitaalltag sinnvoll und praxisnah einsetzen kann. Mit tollen Liedern, Sprüchen, verschiedenen Rhythmusübungen und vielen weiteren kreativen Spielideen.",
    instructor: "Nicole Knorr",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 13,
    title: "Boomwhackers leicht lernen",
    subtitle: "Boomwhackers praktisch einsetzen",
    image: boomwhackers,
    category: "Selbstlernmodule",
    description: "In vielen Kitas sind Boomwhackers vorhanden. Meistens jedoch fehlen dazu konkrete Spiel- & Einsatzmöglichkeiten. Dieser Kurs zeigt dir viele, schnelle umsetzbare Ideen, so dass du gleich loslegen kannst. Das Tolle, es sind keine Vorkenntnisse erforderlich und jeder kann gleich mitspielen.",
    instructor: "Nicole Knorr",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 14,
    title: "Klapper die Cup",
    subtitle: "Becherrhythmen für die Kleinsten",
    image: klapperDieCup,
    category: "Selbstlernmodule",
    description: "Dich erwarten viele kreative Ideen, wie du Becher/Cups musikalisch einsetzen kannst. Wir zeigen dir, wie du kinderleicht eigene Becherrhythmen kreieren kannst, wie man mit Cups Klanggeschichten, Sprüche und Lieder begleiten kann uvm.",
    instructor: "Nicole Knorr & Richard Haus",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 15,
    title: "Trommelissimo",
    subtitle: "Trommeln und Rhythmus",
    image: trommelissimo,
    category: "Selbstlernmodule",
    description: "Lerne wie du mit Spaß & Freude einfache Spieltechniken, Rhythmus- & Taktgefühl kindgerecht vermitteln kannst. Mit vielen Spielideen, Liedern aus aller Welt und praktischen Tipps für alle, die mit Kids von 4-6 Jahren trommeln möchten.",
    instructor: "Nicole Knorr",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 16,
    title: "Bodypercussion leicht lernen",
    subtitle: "Alles rund um Rhythmus mit dem eigenen Körper",
    image: bodypercussion,
    category: "Selbstlernmodule",
    description: "Coole Sprüche & Lieder, sowie Grooves und Spielideen für das Musizieren mit dem eigenen Körper. Wir zeigen dir in diesem Kurs, wie du Bodypercussion sinnvoll und praxisnah in deinem Kita- oder Grundschulalltag sofort einsetzen kannst.",
    instructor: "Nicole Knorr",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 17,
    title: "Cajon - coole Beats auf der Kiste",
    subtitle: "Coole Beats auf der beliebten Kiste",
    image: cajonBeats,
    category: "Selbstlernmodule",
    description: "Das Cajon ist sehr vielseitig einsetzbar! Sei es in der musikalischen Früherziehung mit Kindern ab 3 Jahren oder als vollwertiges Rhythmusinstrument in einer 'Band'. Das Spielen auf dem Cajon fördert die Koordinations- und Konzentrationsfähigkeit der Kinder und kommt zugleich ihrem natürlichen Bewegungsdrang entgegen. In dieser Weiterbildung lernst du alle Grundlagen, um direkt erste Grooves auf der 'Trommelkiste' zu spielen!",
    instructor: "Christian Frenzel",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 18,
    title: "Kreativer Kindertanz",
    subtitle: "Tanz & Bewegung",
    image: kreativerKindertanz,
    category: "Selbstlernmodule",
    description: "In dieser Fortbildung werden Anleitungen und Impulse gegeben, die die Phantasie und die Kreativität der Kinder anregen und sie in ihrem Körperbewusstsein stärken.",
    instructor: "Iva Dekovic",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 19,
    title: "Das kleine ABC des Singens",
    subtitle: "Alles rund um das Singen mit Kindern",
    image: abcDesSingens,
    category: "Selbstlernmodule",
    description: "In dieser Online-Fortbildung lernst du deine Stimme beim Singen besser einzusetzen, ohne dabei heiser zu werden. Es erwarten dich 15 spannende Lektionen mit zahlreichen Übungen für ein leichtes und kraftvolles Singen. Lerne die Grundlagen für gesundes Singen kennen, egal ob du Neuland betrittst oder bereits Erfahrung hast.",
    instructor: "Xenia von Randow",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 20,
    title: "Musiktheorie-Basics | Noten verstehen",
    subtitle: "Hier lernst du die Grundlagen der Notenwerte",
    image: musiktheorieNoten,
    category: "Selbstlernmodule",
    description: "In dieser Online-Fortbildung lernst du alles Nötige rund um das Thema Noten. Du lernst die C-Dur Tonleiter anhand von spielerischen Übungen kennen und anzuwenden.",
    instructor: "Simone Ludwig",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 21,
    title: "Musiktheorie-Basics | Rhythmus verstehen & vermitteln",
    subtitle: "Hier lernst du die Grundlagen der Notenwerte",
    image: musiktheorieRhythmus,
    category: "Selbstlernmodule",
    description: "Simone Ludwig nimmt dich mit auf große Rhythmus-Reise! Verbessere dein Taktgefühl & lerne aktiv die Basics der Rhythmus-Lehre kennen. Zahlreiche Lernspiele, Lieder & Sprechverse werden deine Arbeit mit Kindern bereichern.",
    instructor: "Simone Ludwig",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  },
  {
    id: 22,
    title: "Singende Waldzwerge®",
    subtitle: "Ein Kurs über das Singen und Musizieren im Wald",
    image: singendeWaldzwerge,
    category: "Selbstlernmodule",
    description: "Singende Waldzwerge®️ ist ein einzigartiges musikalische Konzept zum Singen und Musizieren im Wald. In diesem Online-Seminar lernst du das Konzept und eine Vielzahl von Umsetzungsmöglichkeiten kennen.",
    instructor: "Anette Sperling",
    duration: "ca. 7 Zeitstunden",
    lernform: "Dieser Kurs steht dir als \"Selbstlernmodul\" zur Verfügung. Du kannst ihn jederzeit selbständig in unserer Online-Akademie bearbeiten."
  }
];

const FachkraftMusik = () => {
  const [selectedModule, setSelectedModule] = useState<typeof modules[0] | null>(null);
  const [expandedCards, setExpandedCards] = useState<number[]>([]);
  const [api, setApi] = useState<any>();
  
  const toggleExpanded = (index: number) => {
    setExpandedCards(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  const testimonials = [
    {
      name: "Marion Friedsam",
      role: "selbstständige Kursleiterin und Fachkraft für Musik im Elementarbereich",
      text: "Die Ausbildung bei Musikonzept war großartig – praxisnah, hervorragend umsetzbar und begleitet von einer wirklich tollen Community.",
      image: marionFriedsam
    },
    {
      name: "Evi Wagner",
      role: "Erzieherin, Fachkraft für Musik im Elementarbereich",
      text: "Mit MusiKonzept hat man einen \"Goldschatz\" an Möglichkeiten, wie man mit Kindern Musik machen kann. Auf die vielfältigen Inhalte der Live-Webinare greife ich immer wieder gerne zu und setze sie direkt im Kindergarten ein.",
      image: eviWagner
    },
    {
      name: "Anna Wolter",
      role: "Erzieherin, Fachkraft für Musik im Elementarbereich",
      text: "Ich nutze Musikonzept so sehr gerne, weil ich aus einem so breit gefächerten, vielseitigen Angebot schöpfen kann. Die große Auswahl und die kreativen Möglichkeiten inspirieren mich täglich aufs Neue.",
      image: annaWolter
    },
    {
      name: "Sandra Lampe",
      role: "Erzieherin, Fachkraft für Musik im Elementarbereich",
      text: "Ich bin auch nach meiner Ausbildung zur Fachkraft für Musik weiterhin Mitglied bei Musikonzept, weil das vielfältige Angebot mit Kindern zu musizieren wirklich hervorragend ist. Die Vernetzung und der Austausch mit den Fachgruppen und anderen Mitgliedern sind dabei sehr hilfreich. Es gibt unglaublich viele neue Ideen und Tipps zum gemeinsamen Musizieren mit Kindern.",
      image: sandraLampe
    }
  ];
  
  return (
    <div className="min-h-screen">
      <SEO title={`Fachkraft für Musik im Elementarbereich (ZFU) | MusiKonzept`} description={`Staatlich zugelassene Ausbildung (ZFU) zur Fachkraft für Musik im Elementarbereich – berufsbegleitend, online und mit Prüfungszertifikat.`} path="/ausbildung/fachkraft-musik" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-hero-gradient-start via-background to-hero-gradient-end opacity-60" />

        {/* Animierte Hintergrund-Blobs in Markenfarben */}
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-primary/15 blur-3xl animate-blob-drift pointer-events-none" />
        <div className="absolute top-1/3 -right-32 w-[480px] h-[480px] rounded-full bg-accent/15 blur-3xl animate-blob-drift-delayed pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-[360px] h-[360px] rounded-full bg-yellow-300/10 blur-3xl animate-blob-drift pointer-events-none" />

        {/* Floating decorative elements */}
        <div className="absolute top-40 right-0 w-[200px] md:w-[400px] h-[200px] md:h-[400px] opacity-[0.05] animate-float pointer-events-none hidden md:block">
          <img src={notesImg} alt="" className="w-full h-full object-contain" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-[1.3fr,0.7fr] gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" aria-hidden="true" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
                  Staatlich zugelassene Ausbildung · ZfU
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight">
                Fachkraft für{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  Musik im Elementarbereich
                </span>
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                Die berufsbegleitende Weiterbildung für pädagogische Fachkräfte,
                die Musik professionell in ihren Arbeitsalltag integrieren möchten.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4 items-start">
                <Button
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
                  asChild
                >
                  <Link to="/ausbildung/fachkraft-musik/kaufen">
                    Jetzt anmelden
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 border-2 hover:bg-accent/10 transition-all duration-300 hover:scale-105 group"
                  asChild
                >
                  <a href="/webinar/fachkraft-musik" rel="noopener noreferrer">
                    Kostenloses Infowebinar
                    <PlayCircle className="ml-2 h-5 w-5 transition-transform group-hover:scale-110" />
                  </a>
                </Button>
              </div>
            </div>

            {/* Right Image with Floating Badge */}
            <div className="relative animate-fade-in mt-8 lg:mt-0" style={{ animationDelay: "0.2s" }}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-[3/4] max-h-[320px] lg:max-h-[720px] max-w-md lg:max-w-none mx-auto">
                <img
                  src={heroImage.url}
                  alt="Fachkraft für Musik im Elementarbereich"
                  className="w-full h-full object-cover"
                />
                {/* Decorative Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
              </div>

              {/* Runder Floating Badge: Einstieg jederzeit möglich */}
              <div className="absolute -top-2 -right-2 lg:-top-6 lg:-right-6 z-20 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-accent/40 blur-2xl scale-110 animate-pulse" aria-hidden="true" />
                  <div className="relative w-16 h-16 md:w-20 md:h-20 lg:w-44 lg:h-44 rounded-full bg-gradient-to-br from-accent to-yellow-400 text-accent-foreground shadow-2xl flex flex-col items-center justify-center text-center p-1.5 md:p-2 lg:p-4 -rotate-6 hover:rotate-0 transition-transform duration-500">
                    <span className="text-[10px] md:text-xs lg:text-lg font-bold uppercase tracking-wide leading-tight">Einstieg<br/>jederzeit<br/>möglich</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-6 border-y border-border/40 bg-muted/20">
        <div className="container mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-0 text-center">
            {/* Keine Bewertung ohne Quelle (Frage 297, Sitzung „Aufräumen“) */}
            <div className="flex items-center gap-2 sm:px-6">
              <Users className="w-5 h-5 text-primary" />
              <span className="font-medium text-foreground">500+</span>
              <span className="text-muted-foreground">Aktive Mitglieder</span>
            </div>

            <div className="hidden sm:block w-px h-5 bg-border/60" />

            <a
              href="/foerdermoeglichkeiten"
              className="group flex items-center gap-2 sm:px-6 text-foreground hover:text-accent transition-colors"
            >
              <BadgeEuro className="w-5 h-5 text-primary" />
              <span className="font-medium">Förderfähig – bis zu 500 € Zuschuss in NRW</span>
              <ArrowRight className="w-4 h-4 text-accent transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </section>

      {/* Award Badge Section - Enhanced */}
      <section className="py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background" />
        
        {/* Subtle glow effect */}
        <div className="absolute inset-0 flex items-center justify-center opacity-30">
          <div className="w-[600px] h-[300px] bg-accent/20 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <Card className="bg-card/50 backdrop-blur-xl border-accent/20 shadow-2xl hover:shadow-accent/10 transition-all duration-500 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-accent/5 via-primary/5 to-accent/5 opacity-50" />
              
              <CardContent className="p-8 md:p-12 relative z-10">
                <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                  {/* Logo with glow */}
                  <div className="relative flex-shrink-0 animate-fade-in">
                    <div className="absolute inset-0 bg-accent/20 rounded-full blur-2xl animate-pulse" />
                    <div className="relative w-36 h-36 md:w-44 md:h-44">
                      <img 
                        src={awardLogo} 
                        alt="Studienpreis 2025 - Studienangebot des Jahres - Nominiert" 
                        className="w-full h-full object-contain drop-shadow-2xl"
                      />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 text-center md:text-left space-y-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                    <div className="space-y-2">
                      <Badge className="bg-accent/20 text-accent border-accent/30 mb-2">
                        🏆 Auszeichnung 2025
                      </Badge>
                      <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-foreground leading-tight">
                        Nominiert für den{" "}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-primary to-accent">
                          Studienpreis 2025
                        </span>
                      </h3>
                    </div>
                    
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                      Unsere Ausbildung wurde vom <strong className="text-foreground">Bundesverband der Fernstudienanbieter</strong> als 
                      herausragendes Studienangebot nominiert – eine Auszeichnung für Qualität und Innovation in der Weiterbildung.
                    </p>
                    
                    {/* Small stats or additional info */}
                    <div className="flex flex-wrap items-center gap-6 pt-4 justify-center md:justify-start">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                        <span className="text-muted-foreground">Kategorie: Studienangebot des Jahres</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Award className="w-4 h-4 text-accent" />
                        <span className="text-muted-foreground">2025</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Einzigartiges Konzept Section - 3D Cards with Flip */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/20 to-background" />
        
        {/* Floating decorative element */}
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] opacity-[0.06] animate-float-delayed pointer-events-none">
          <img src={drumImg} alt="" className="w-full h-full object-contain" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16 space-y-4 animate-fade-in">
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground">
              Einzigartiges{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary">
                Konzept
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Deine Vorteile auf einen Blick
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Card 1 */}
            <Card className="group relative bg-card/40 backdrop-blur-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden animate-fade-in border-0">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardContent className="p-8 text-center space-y-4 relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary group-hover:bg-white/20 group-hover:text-white group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <Video className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-foreground group-hover:text-white transition-colors duration-500">Video-Fortbildungen</h3>
                <p className="text-muted-foreground group-hover:text-white/90 leading-relaxed transition-colors duration-500">
                  Über 50 Video-Fortbildungen zu allen relevanten Themen
                </p>
              </CardContent>
            </Card>

            {/* Card 2 */}
            <Card className="group relative bg-card/40 backdrop-blur-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden animate-fade-in border-0" style={{ animationDelay: "0.1s" }}>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardContent className="p-8 text-center space-y-4 relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary group-hover:bg-white/20 group-hover:text-white group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <Laptop className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-foreground group-hover:text-white transition-colors duration-500">Live-Webinare</h3>
                <p className="text-muted-foreground group-hover:text-white/90 leading-relaxed transition-colors duration-500">
                  Regelmäßige Live-Webinare zum Austausch und Vernetzen
                </p>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="mt-2 text-primary group-hover:text-white group-hover:bg-white/20 transition-colors duration-500"
                  asChild
                >
                  <a href="/termine?kategorie=Ausbildungs-Webinare+(Fachkraft+für+Musik)" target="_blank" rel="noopener noreferrer">
                    Alle Termine ansehen →
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* Card 3 */}
            <Card className="group relative bg-card/40 backdrop-blur-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden animate-fade-in border-0" style={{ animationDelay: "0.2s" }}>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardContent className="p-8 text-center space-y-4 relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary group-hover:bg-white/20 group-hover:text-white group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <Users className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-foreground group-hover:text-white transition-colors duration-500">Community</h3>
                <p className="text-muted-foreground group-hover:text-white/90 leading-relaxed transition-colors duration-500">
                  Exklusive Community mit über 500 aktiven Mitgliedern
                </p>
              </CardContent>
            </Card>

            {/* Card 4 */}
            <Card className="group relative bg-card/40 backdrop-blur-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden animate-fade-in border-0" style={{ animationDelay: "0.3s" }}>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardContent className="p-8 text-center space-y-4 relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary group-hover:bg-white/20 group-hover:text-white group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <CheckCircle className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-foreground group-hover:text-white transition-colors duration-500">Stundenbilder</h3>
                <p className="text-muted-foreground group-hover:text-white/90 leading-relaxed transition-colors duration-500">
                  Sofort einsetzbare Stundenbilder für deine Praxis
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Erklärvideo Section - Modern Split Layout */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/20 to-background" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            {/* Left - Content */}
            <div className="space-y-8 animate-fade-in">
              <div>
                <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">
                  Video-Erklärung
                </Badge>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                  Schau dir das{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                    Erklärvideo
                  </span>{" "}
                  an!
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground leading-relaxed">
                Im Video erfährst du alles über die Ausbildung, die Inhalte und den Ablauf. 
                Überzeuge dich selbst von unserem einzigartigen Konzept.
              </p>
              
              <div className="space-y-4 pt-4">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto font-semibold bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  asChild
                >
                  <Link to="/ausbildung/fachkraft-musik/kaufen">
                    Jetzt anmelden
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full sm:w-auto font-semibold rounded-full px-8 border-2 ml-0 sm:ml-4"
                  asChild
                >
                  <a href="https://calendar.app.google/LJnUyaadvFeQhR2P7" target="_blank" rel="noopener noreferrer">
                    Beratungstermin buchen
                  </a>
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground pt-4">
                Oder schreibe uns direkt über das{" "}
                <a href="/kontakt" className="text-primary hover:underline font-semibold">
                  Kontaktformular
                </a>
              </p>
            </div>
            
            {/* Right - Video */}
            <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-card border border-border/50">
                <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
                  <iframe 
                    src="https://www.loom.com/embed/7665866db9e1450c89c03a73e4179e17?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true" 
                    frameBorder="0" 
                    allowFullScreen
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                    title="Erklärvideo Fachkraft für Musik"
                    className="rounded-3xl"
                  />
                </div>
              </div>
              
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-accent/20 rounded-full blur-2xl"></div>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary/20 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Berufsbegleitende Weiterbildung - Asymmetric Layout */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 space-y-4 animate-fade-in">
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground">
              Berufsbegleitende Weiterbildung
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Für pädagogische Fachkräfte
            </p>
          </div>
          
          <div className="grid lg:grid-cols-[0.7fr,1.3fr] gap-16 items-center max-w-6xl mx-auto">
            {/* Left - Zertifikat Image with 3D Effect */}
            <div className="relative group animate-fade-in">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-50"></div>
              
              <div className="relative">
                <img 
                  src={zertifikatImage.url} 
                  alt="Zertifikat Fachkraft für Musik" 
                  className="relative w-full h-auto rounded-3xl shadow-2xl group-hover:scale-105 transition-transform duration-500"
                />
                {/* Decorative corner badge */}
                <div className="absolute -top-4 -right-4 bg-accent text-accent-foreground rounded-full w-20 h-20 flex items-center justify-center shadow-xl font-bold text-sm group-hover:rotate-12 transition-transform duration-500">
                  <div className="text-center">
                    <div>12</div>
                    <div className="text-xs">Monate</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right - Details */}
            <div className="space-y-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Diese staatlich zugelassene Ausbildung bereitet dich optimal darauf vor, 
                Musik professionell in deinen pädagogischen Alltag zu integrieren.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Detail Cards */}
                <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 space-y-2">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <p className="font-semibold text-foreground">Dauer: 12 Monate</p>
                    <p className="text-sm text-muted-foreground">inkl. Prüfungsphase</p>
                  </CardContent>
                </Card>

                <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 space-y-2">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <p className="font-semibold text-foreground">Kosten: {FFM_PREIS}</p>
                    <p className="text-sm text-muted-foreground">
                      Mitglieder {FFM_MITGLIED}; ihr eigenes Abo ruht ab der Buchung ein Jahr
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-lg transition-all duration-300 sm:col-span-2">
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <p className="font-semibold text-foreground">Zielgruppe</p>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      ErzieherInnen, pädagogische Fachkräfte, KursleiterInnen und alle, 
                      die professionell mit Musik im Elementarbereich arbeiten möchten
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-accent/10 to-primary/10 backdrop-blur-sm border-accent/20 hover:shadow-lg transition-all duration-300 sm:col-span-2">
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                        <Award className="h-5 w-5 text-accent" />
                      </div>
                      <p className="font-semibold text-foreground">Abschluss</p>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Zertifikat "Fachkraft für Musik im Elementarbereich" nach bestandener Prüfung
                    </p>
                  </CardContent>
                </Card>

                {/* ZFU Certification Card */}
                <Card className="bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 backdrop-blur-sm border-primary/20 hover:shadow-xl transition-all duration-300 sm:col-span-2">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="w-24 h-24 flex-shrink-0">
                        <img 
                          src={zfuLogo} 
                          alt="Staatliche Zentralstelle für Fernunterricht - ZFU zugelassen" 
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex-1 space-y-2 text-center sm:text-left">
                        <p className="font-bold text-foreground text-lg">
                          Staatlich zugelassen
                        </p>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Diese Weiterbildung ist von der Staatlichen Zentralstelle für Fernunterricht (ZFU) geprüft und zugelassen.
                        </p>
                        <div className="flex items-center gap-2 justify-center sm:justify-start pt-2">
                          <Badge variant="outline" className="text-xs font-mono bg-background/50">
                            Zulassungs-Nr. 7432522c
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dein Weg zur Fachkraft Section - Interactive */}
      <InteractivePathSection />

      {/* Termine Section */}
      <TermineSection />

      {/* Lernmodule Section - Interactive Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background to-muted/30" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16 space-y-4 animate-fade-in">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
              Lernmodule
            </Badge>
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground">
              Deine{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary">
                Lernmodule
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Hier findest du eine Übersicht über die verschiedenen Lernmodule, auf die du im Rahmen der 
              Ausbildung Zugriff hast. Klicke auf eine Kachel, um mehr zu erfahren.
            </p>
          </div>
          
          {/* Modules Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {modules.map((module, index) => {
              const isSelbstlernmodul = module.category === "Selbstlernmodule";
              
              return (
                <Card 
                  key={module.id}
                  className={`group relative backdrop-blur-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden cursor-pointer border-0 animate-fade-in ${
                    isSelbstlernmodul ? "bg-accent/5" : "bg-card/50"
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={() => setSelectedModule(module)}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden">
                    <img 
                      src={module.image} 
                      alt={module.title}
                      className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    
                    {/* Play Icon Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-xl ${
                        isSelbstlernmodul ? "bg-accent/90" : "bg-primary/90"
                      }`}>
                        <PlayCircle className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 right-4">
                      <Badge className={`backdrop-blur-sm ${
                        isSelbstlernmodul 
                          ? "bg-accent/90 text-accent-foreground" 
                          : "bg-primary/90 text-primary-foreground"
                      }`}>
                        {module.category}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <CardContent className="p-6 space-y-2">
                    <h3 className={`text-lg font-bold transition-colors ${
                      isSelbstlernmodul 
                        ? "text-foreground group-hover:text-accent" 
                        : "text-foreground group-hover:text-primary"
                    }`}>
                      {module.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {module.subtitle}
                    </p>
                  </CardContent>
                  
                  {/* Hover Arrow */}
                  <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowRight className={`w-5 h-5 ${
                      isSelbstlernmodul ? "text-accent" : "text-primary"
                    }`} />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Module Dialog/Popup */}
      <Dialog open={!!selectedModule} onOpenChange={() => setSelectedModule(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedModule && (
            <>
              <DialogHeader>
                <div className="mb-4">
                  <Badge className="bg-primary/10 text-primary mb-3">
                    {selectedModule.category}
                  </Badge>
                  <DialogTitle className="text-3xl font-bold text-foreground mb-2">
                    {selectedModule.title}
                  </DialogTitle>
                  <DialogDescription className="text-lg text-muted-foreground">
                    {selectedModule.subtitle}
                  </DialogDescription>
                </div>
              </DialogHeader>
              
              {/* Module Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl mb-6">
                <img 
                  src={selectedModule.image} 
                  alt={selectedModule.title}
                  className="w-full h-auto"
                />
              </div>
              
              {/* Module Description */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-primary" />
                    Über dieses Modul
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {selectedModule.description}
                  </p>
                </div>
                
                {/* Module Details */}
                {(selectedModule.instructor || selectedModule.duration) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedModule.instructor && (
                      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-0">
                        <CardContent className="p-6 space-y-2">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="w-4 h-4 text-primary" />
                            <span>Dozent/in</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {selectedModule.instructor}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                    
                    {selectedModule.duration && (
                      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-0">
                        <CardContent className="p-6 space-y-2">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="w-4 h-4 text-primary" />
                            <span>Dauer</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {selectedModule.duration}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
                
                {/* Lernform Info - nur für Selbstlernmodule */}
                {selectedModule.lernform && (
                  <Card className="bg-accent/10 border border-accent/20">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <BookOpen className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-foreground mb-2">Lernform</h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {selectedModule.lernform}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Section - Magazine Style Layout */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 space-y-4 animate-fade-in">
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground">
              Dein Team hinter der{" "}
              <span className="relative inline-block">
                Ausbildung
                <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary rounded-full"></div>
              </span>
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-[1fr,1.2fr] gap-16 items-start max-w-6xl mx-auto">
            {/* Left - Team Image */}
            <div className="relative animate-fade-in">
              <Card className="overflow-hidden shadow-2xl border-0 group">
                <img 
                  src={teamImage} 
                  alt="MusiKonzept Team" 
                  className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
                />
              </Card>
              {/* Decorative accent */}
              <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl"></div>
            </div>
            
            {/* Right - Content */}
            <div className="space-y-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Wir sind <span className="font-semibold text-primary">Kati Breuer</span>, 
                  <span className="font-semibold text-primary"> Nicole Knorr</span> und 
                  <span className="font-semibold text-primary"> Daniel Sommer</span> – 
                  dein Team hinter der Ausbildung zur Fachkraft für Musik im Elementarbereich.
                </p>
                
                <p>
                  Mit jahrelanger Erfahrung in der musikalischen Früherziehung und Pädagogik 
                  haben wir ein Ausbildungskonzept entwickelt, das praxisnah, flexibel und 
                  sofort umsetzbar ist.
                </p>
                
                <p>
                  Wir begleiten dich persönlich auf deinem Weg und stehen dir mit Rat und Tat 
                  zur Seite – in Live-Webinaren, der Community und darüber hinaus.
                </p>
              </div>
              
              <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-0">
                <CardContent className="p-8 space-y-4">
                  <p className="text-lg font-semibold text-foreground italic">
                    Mit musikalischen Grüßen,
                  </p>
                  <div className="relative h-24 overflow-hidden">
                    <img 
                      src={signaturesImage} 
                      alt="Unterschriften" 
                      className="h-full w-auto object-contain"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Kati, Nicole & Daniel
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-20 bg-gradient-to-br from-accent/5 via-background to-primary/5 overflow-hidden">
        {/* Watercolor decorations */}
        <div className="absolute top-10 left-10 w-64 h-64 opacity-10 pointer-events-none animate-pulse">
          <img src={notesImg} alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute bottom-10 right-10 w-52 h-52 opacity-10 pointer-events-none animate-pulse" style={{ animationDelay: '0.5s' }}>
          <img src={drumImg} alt="" className="w-full h-full object-contain" />
        </div>
        
        <div className="container relative mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium">
                Erfahrungen
              </span>
            </div>
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Das sagen Teilnehmer:innen über die Ausbildung
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Echte Erfahrungen von Fachkräften, die ihre musikalische Arbeit mit unserer Ausbildung transformiert haben
            </p>
          </div>
          
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 4000,
              }),
            ]}
            className="w-full max-w-6xl mx-auto"
          >
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial, index) => {
                const maxLength = 200;
                const isLongText = testimonial.text.length > maxLength;
                const isExpanded = expandedCards.includes(index);
                const displayText = isLongText && !isExpanded 
                  ? truncateText(testimonial.text, maxLength)
                  : testimonial.text;

                return (
                  <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <Card 
                      className="relative p-8 h-full bg-gradient-to-br from-background via-muted/20 to-background border-border/50 hover:shadow-2xl hover:shadow-accent/10 transition-all duration-500 hover:-translate-y-3 hover:scale-[1.02] group overflow-hidden flex flex-col"
                      style={{ 
                        animationDelay: `${index * 150}ms`,
                        animation: 'fade-in 0.6s ease-out forwards'
                      }}
                    >
                      {/* Quote decoration */}
                      <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Quote className="w-16 h-16 text-primary" />
                      </div>
                      
                      {/* Content */}
                      <div className="relative space-y-6 flex flex-col flex-1">
                        {/* Profile Image */}
                        <div className="relative w-20 h-20 mx-auto">
                          <img 
                            src={testimonial.image} 
                            alt={testimonial.name}
                            className="w-full h-full rounded-full object-cover ring-4 ring-primary/20 group-hover:ring-primary/40 transition-all duration-300"
                          />
                          <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <Quote className="w-5 h-5 text-primary-foreground" />
                          </div>
                        </div>
                        
                        {/* Rating Stars */}
                        <div className="flex gap-1 justify-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-5 h-5 fill-accent text-accent"
                            />
                          ))}
                        </div>
                        
                        {/* Testimonial Text */}
                        <div className="flex-1">
                          <p className="text-foreground leading-relaxed text-center italic">
                            "{displayText}"
                          </p>
                          
                          {isLongText && (
                            <button
                              onClick={() => toggleExpanded(index)}
                              className="mt-2 text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors mx-auto"
                            >
                              {isExpanded ? (
                                <>
                                  Weniger lesen
                                  <ChevronUp className="w-4 h-4" />
                                </>
                              ) : (
                                <>
                                  Mehr lesen
                                  <ChevronDown className="w-4 h-4" />
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        
                        {/* Author */}
                        <div className="pt-6 border-t border-border/50 text-center">
                          <p className="font-semibold text-foreground">{testimonial.name}</p>
                          <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                        </div>
                      </div>
                    </Card>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>
        </div>
      </section>

      {/* Final CTA Section - Bold & Modern */}
      <section className="py-24 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-accent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-10">
            <div className="space-y-6 animate-fade-in">
              <Badge className="bg-white/20 text-white hover:bg-white/30 border-white/30">
                Starte jetzt deine Reise
              </Badge>
              
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-bold text-white leading-tight">
                Bereit für deine musikalische Zukunft?
              </h2>
              
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
                Starte jetzt deine Ausbildung und werde Teil der MusiKonzept-Community. 
                Über 500 Fachkräfte haben bereits ihre musikalische Reise mit uns begonnen.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <Button 
                size="lg" 
                className="bg-white hover:bg-white/90 text-primary font-semibold rounded-full px-10 shadow-2xl hover:scale-105 transition-all duration-300"
                asChild
              >
                <Link to="/ausbildung/fachkraft-musik/kaufen">
                  Jetzt anmelden
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                className="bg-white/10 hover:bg-white text-white hover:text-primary font-semibold rounded-full px-10 backdrop-blur-sm border-2 border-white/30 transition-all duration-300"
                asChild
              >
                <a href="/webinar/fachkraft-musik" rel="noopener noreferrer">
                  Kostenloses Infowebinar
                </a>
              </Button>
            </div>

            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-8 pt-12 animate-fade-in" style={{ animationDelay: "0.3s" }}>
              <div className="space-y-2">
                <div className="text-4xl font-bold text-white">500+</div>
                <div className="text-white/80">Teilnehmer</div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold text-white">50+</div>
                <div className="text-white/80">Video-Kurse</div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold text-white">330 h</div>
                <div className="text-white/80">Umfang laut Zertifikat</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fördermöglichkeiten-Hinweis */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8 shadow-sm">
            <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                <GraduationCap className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1 space-y-2">
                <h3 className="text-xl font-bold text-foreground">
                  Fördermöglichkeiten prüfen
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Je nach Bundesland und persönlicher Situation kann deine Weiterbildung
                  förderfähig sein – z.&nbsp;B. über den Bildungsscheck NRW, QualiScheck oder
                  Weiterbildungsbonus. Anträge müssen meist vor Beginn der Weiterbildung gestellt werden.
                </p>
              </div>
              <a
                href="/foerdermoeglichkeiten"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap"
              >
                Förderungen ansehen
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FachkraftMusik;
